export type PeriodSelection = { readonly kind: 'year'; readonly year: number }
    | { readonly kind: 'date'; readonly date: string }
    | { readonly kind: 'range'; readonly from: string; readonly to: string }
    | { readonly kind: 'current' };
export type TemporalBasis = 'calculationYear' | 'incomeYear' | 'openingDate' | 'exitDate' | 'fullAccrualRange' | 'calendarRange' | 'examYear' | 'academicPeriod' | 'reviewOnly' | 'timeless';
export interface PolicyPeriod {
    readonly periodId: string;
    readonly policyRevisionId: string;
    readonly coverageFrom: string | null;
    readonly coverageTo: string | null;
    readonly legalEffectiveTo: string | null;
    readonly verification: 'verified' | 'unverified' | 'invalid';
    readonly verifiedAt: string | null;
    readonly reviewAfter: string | null;
    readonly sourceIds: readonly string[];
}
export interface PolicyScope {
    readonly modeId: string;
    readonly label: string;
    readonly temporalBasis: TemporalBasis;
    readonly coverageRule: 'year' | 'point' | 'range' | 'none';
    readonly matches: readonly { readonly selector: string; readonly values: readonly string[] }[];
    readonly dateFields: readonly string[];
    readonly parameterIds: readonly string[];
    readonly periods: readonly PolicyPeriod[];
}
export interface PeriodTool {
    readonly toolId: string;
    readonly title: string;
    readonly path: string;
    readonly configVersion: string;
    readonly classification: 'timeless' | 'remote-config';
    readonly sources: readonly { readonly sourceId: string; readonly url: string; readonly title: string; readonly publisher: string; readonly accessState?: 'reachable' | 'unavailable' | 'unexpected-content' | 'unchecked'; readonly accessedAt?: string | null }[];
    readonly scopes: readonly PolicyScope[];
}
export interface ResolvedPolicySnapshot {
    readonly identity: string;
    readonly toolId: string;
    readonly modeId: string;
    readonly periodId: string;
    readonly policyRevisionId: string;
    readonly auditDate: string;
    readonly selection: PeriodSelection;
    readonly periods: readonly PolicyPeriod[];
    readonly reviewState: 'current' | 'review-due';
    readonly future: boolean;
}
export type PeriodResolution = { readonly availability: 'available'; readonly snapshot: ResolvedPolicySnapshot }
    | { readonly availability: 'unverified' | 'invalid'; readonly message: string };
const resolvedSnapshots = new WeakSet<object>();

export function isResolvedPolicySnapshot(value: unknown): value is ResolvedPolicySnapshot {
    return value !== null && typeof value === 'object' && resolvedSnapshots.has(value);
}

export function isPolicyDate(raw: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
    const date = new Date(raw + 'T00:00:00Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === raw;
}

export function istanbulPolicyDate(now: Date = new Date()): string {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)?.value;
    const year = part('year'), month = part('month'), day = part('day');
    if (!year || !month || !day) throw new Error('İstanbul takvim günü üretilemedi.');
    return year + '-' + month + '-' + day;
}

export function resolveToolPeriod(catalog: readonly PeriodTool[], toolId: string, modeId: string, selection: PeriodSelection, today: string = istanbulPolicyDate()): PeriodResolution {
    const invalid = (message: string): PeriodResolution => ({ availability: 'invalid', message });
    if (!isPolicyDate(today)) return invalid('Denetim tarihi geçersiz.');
    const tool = catalog.find(item => item.toolId === toolId);
    const scope = tool?.scopes.find(item => item.modeId === modeId);
    if (!tool || !scope) return invalid('Hesaplama dönemi veya mod kaydı bulunamadı.');
    let from: string, to: string;
    if (selection.kind === 'year') {
        if (!Number.isInteger(selection.year) || selection.year < 1 || selection.year > 9999) return invalid('Hesaplama yılı geçersiz.');
        const year = String(selection.year).padStart(4, '0');
        from = year + '-01-01'; to = year + '-12-31';
    } else if (selection.kind === 'date') {
        from = selection.date; to = selection.date;
    } else if (selection.kind === 'range') {
        from = selection.from; to = selection.to;
    } else if (selection.kind === 'current') {
        from = today; to = today;
    } else return invalid('Dönem seçimi geçersiz.');
    if (!isPolicyDate(from) || !isPolicyDate(to) || from > to) return invalid('Hesaplama tarihi veya aralığı geçersiz.');
    if (scope.coverageRule === 'year' && selection.kind !== 'year' && selection.kind !== 'current') return invalid('Bu hesaplama için yıl seçin.');
    if (scope.coverageRule === 'point' && selection.kind !== 'date') return invalid('Bu hesaplama için işlem tarihi girin.');
    if (scope.coverageRule === 'range' && selection.kind !== 'range') return invalid('Bu hesaplama için başlangıç ve bitiş tarihi girin.');
    if (scope.coverageRule === 'year' && selection.kind === 'current') { from = today.slice(0, 4) + '-01-01'; to = today.slice(0, 4) + '-12-31'; }
    const year = from.slice(0, 4);
    const candidates = scope.periods.filter(period => scope.coverageRule === 'none'
        || (scope.coverageRule === 'year' ? period.periodId === year
            : (period.coverageFrom === null || period.coverageFrom <= to) && (period.coverageTo === null || period.coverageTo >= from)));
    if (!candidates.length || candidates.some(period => period.verification === 'unverified')) return { availability: 'unverified', message: 'Seçilen dönem için doğrulanmış veri yok. Desteklenen bir dönemi seçin.' };
    if (scope.coverageRule === 'year' && candidates.some(period => (period.coverageFrom !== null && period.coverageFrom > from) || (period.coverageTo !== null && period.coverageTo < to))) return { availability: 'unverified', message: 'Seçilen yılın tamamı doğrulanmış veriyle kapsanmıyor.' };
    if (candidates.some(period => period.verification === 'invalid' || (period.legalEffectiveTo !== null && to > period.legalEffectiveTo))) return invalid('Seçilen dönem için kuralın geçersizliği doğrulanmıştır.');
    if (scope.coverageRule !== 'year' && scope.coverageRule !== 'none') {
        const ordered = [...candidates].sort((left, right) => (left.coverageFrom ?? '').localeCompare(right.coverageFrom ?? ''));
        let cursor = from;
        for (const period of ordered) {
            if (period.coverageFrom !== null && period.coverageFrom > cursor) return { availability: 'unverified', message: 'Tarih aralığının tamamı doğrulanmış veriyle kapsanmıyor.' };
            if (period.coverageTo === null || period.coverageTo >= to) { cursor = to; break; }
            const next = new Date(period.coverageTo + 'T00:00:00Z'); next.setUTCDate(next.getUTCDate() + 1);
            cursor = next.toISOString().slice(0, 10);
        }
        if (cursor < to || !ordered.some(period => period.coverageTo === null || period.coverageTo >= to)) return { availability: 'unverified', message: 'Tarih aralığının tamamı doğrulanmış veriyle kapsanmıyor.' };
    }
    const periods = Object.freeze(candidates.map(period => Object.freeze({ ...period, sourceIds: Object.freeze([...period.sourceIds]) })));
    const periodId = scope.coverageRule === 'year' ? year : scope.coverageRule === 'point' ? from : scope.coverageRule === 'range' ? from + '..' + to : periods.map(period => period.periodId).join('+');
    const policyRevisionId = periods.map(period => period.policyRevisionId).join('+');
    const snapshot: ResolvedPolicySnapshot = Object.freeze({ identity: [toolId, modeId, periodId, policyRevisionId].join(':'), toolId, modeId, periodId, policyRevisionId,
        auditDate: today, selection: Object.freeze({ ...selection }), periods,
        reviewState: periods.some(period => period.reviewAfter !== null && today >= period.reviewAfter) ? 'review-due' : 'current',
        future: scope.coverageRule !== 'none' && from > today });
    resolvedSnapshots.add(snapshot);
    return { availability: 'available', snapshot };
}

export function validatePeriodCatalog(catalog: readonly PeriodTool[], parameterKeys?: ReadonlyMap<string, readonly string[]>): void {
    const ids = new Set<string>();
    for (const tool of catalog) {
        if (!tool.toolId || ids.has(tool.toolId) || !tool.scopes.length) throw new Error('Dönem kataloğunda eksik veya çift araç.');
        ids.add(tool.toolId);
        const modes = new Set<string>(), sources = new Set(tool.sources.map(source => source.sourceId));
        if (sources.size !== tool.sources.length) throw new Error('Dönem kataloğunda çift kaynak var.');
        for (const scope of tool.scopes) {
            if (!scope.modeId || modes.has(scope.modeId) || !scope.periods.length || !scope.parameterIds.length) throw new Error('Dönem kataloğunda eksik veya çift mod/bağımlılık.');
            modes.add(scope.modeId);
            if (parameterKeys && (!parameterKeys.has(tool.toolId) || scope.parameterIds.some(key => !parameterKeys.get(tool.toolId)?.includes(key)))) throw new Error('Dönem parametre bağımlılığı bulunamadı.');
            if (scope.coverageRule === 'none' && scope.periods.length !== 1) throw new Error('Dönemsiz mod tek kural sürümü taşımalıdır.');
            const periodIds = new Set<string>();
            for (const period of scope.periods) {
                if (!period.periodId || !period.policyRevisionId || periodIds.has(period.periodId)
                    || period.sourceIds.some(id => !sources.has(id))
                    || (period.verification === 'verified' && (period.verifiedAt === null || !isPolicyDate(period.verifiedAt)))
                    || (period.verifiedAt !== null && !isPolicyDate(period.verifiedAt))
                    || (period.reviewAfter !== null && (!isPolicyDate(period.reviewAfter) || (period.verifiedAt !== null && period.reviewAfter <= period.verifiedAt)))
                    || [period.coverageFrom, period.coverageTo, period.legalEffectiveTo].some(date => date !== null && !isPolicyDate(date))
                    || (period.coverageFrom !== null && period.coverageTo !== null && period.coverageFrom > period.coverageTo)) throw new Error('Dönem alanları veya kaynak bağımlılıkları geçersiz.');
                periodIds.add(period.periodId);
            }
            const ranges = [...scope.periods].sort((left, right) => (left.coverageFrom ?? '').localeCompare(right.coverageFrom ?? ''));
            for (let index = 1; index < ranges.length; index++) {
                const previous = ranges[index - 1], current = ranges[index];
                if (previous && current && (previous.coverageTo === null || current.coverageFrom === null || previous.coverageTo >= current.coverageFrom)) throw new Error('Aynı modda çakışan dönemler var.');
            }
        }
    }
}
